import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/apiClient";
import { FileText, Settings, LogOut, Plus, Menu, X, ShieldCheck, ChevronsLeft, ChevronsRight, User as UserIcon, ClipboardList } from "lucide-react";

const baseNav = [
  { to: "/quotes", label: "Calculations", icon: FileText, testid: "nav-calculations", page: "quotes" },
  { to: "/surveys", label: "Surveys", icon: ClipboardList, testid: "nav-surveys", page: "surveys" },
  { to: "/profile", label: "Profile", icon: UserIcon, testid: "nav-profile", page: "profile" },
];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("qm.sidebar.collapsed") === "1"; } catch { return false; }
  });
  const [pendingCount, setPendingCount] = useState(0);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users?status=pending").then((r) => setPendingCount(r.data.length)).catch(() => {});
  }, [isAdmin]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("qm.sidebar.collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const can = (page) => isAdmin || (user?.pages || []).includes(page);
  const navItems = [
    ...baseNav.filter((n) => can(n.page)),
    ...(isAdmin ? [{ to: "/catalog", label: "Settings", icon: Settings, testid: "nav-settings" }] : []),
    ...(can("users") ? [{ to: "/users", label: "Users", icon: ShieldCheck, testid: "nav-users", badge: isAdmin ? pendingCount : 0 }] : []),
  ];

  const sidebarInner = (mode) => {
    // mode: "desktop-expanded" | "desktop-collapsed" | "mobile"
    const isCollapsed = mode === "desktop-collapsed";
    return (
      <div className="flex flex-col h-full">
        {/* Logo */}
        {!isCollapsed && (
          <div className="pt-6 pb-5 flex items-center gap-3 px-5">
            <div className="bg-white/95 rounded-md p-1.5 flex-shrink-0">
              <img src="/brand/entry-group-logo.jpeg" alt="Entry Group" className="h-8 w-auto rounded-sm" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-base tracking-tight">Quote Matrix</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-brand-orange font-semibold">By Entry Group</div>
            </div>
          </div>
        )}
        {isCollapsed && <div className="pt-6" />}

        {/* Quick Create CTA */}
        {can("quote_create") && <div className={`${isCollapsed ? "px-2" : "px-4"} mb-4`}>
          <button
            onClick={() => { setMobileOpen(false); navigate("/quotes/new"); }}
            data-testid="quick-create-button"
            title="Quick Create"
            className={`w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-brand-orange hover:bg-orange-500 text-white text-xs uppercase tracking-wider font-semibold transition-colors shadow-sm ${isCollapsed ? "px-0" : ""}`}
          >
            <Plus size={14} /> {!isCollapsed && "Quick Create"}
          </button>
        </div>}

        {/* Nav items */}
        <nav className={`flex-1 ${isCollapsed ? "px-2" : "px-3"} space-y-0.5 overflow-y-auto`}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              onClick={() => setMobileOpen(false)}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 ${isCollapsed ? "justify-center px-0" : "px-3"} h-10 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-brand-orange" />
                  )}
                  <item.icon size={16} className={`flex-shrink-0 ${isActive ? "text-brand-orange" : "text-slate-400 group-hover:text-white"}`} />
                  {!isCollapsed && <span className="flex-1">{item.label}</span>}
                  {item.badge > 0 && (
                    <span className={`bg-brand-orange text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1 ${isCollapsed ? "absolute top-1 right-1" : ""}`}>{item.badge}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User block + collapse toggle */}
        <div className={`pb-4 mt-4 border-t border-white/10 pt-4 ${isCollapsed ? "px-2" : "px-3"}`}>
          {!isCollapsed && (
            <div className="px-3 mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-0.5">
                {isAdmin ? "Administrator" : isManager ? "Depot Manager" : "Estimator"}
              </div>
              <div className="text-sm font-medium text-white truncate" data-testid="current-user-name">
                {user?.name || user?.email}
              </div>
              {user?.branch && (
                <div className="text-[10px] uppercase tracking-wider text-brand-orange font-semibold mt-0.5 truncate" data-testid="current-user-branch">
                  {user.branch}
                </div>
              )}
            </div>
          )}

          {mode !== "mobile" && (
            <button
              onClick={toggleCollapsed}
              data-testid="sidebar-collapse-toggle"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={`w-full inline-flex items-center justify-center gap-2 h-8 mb-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white text-[10px] uppercase tracking-wider font-semibold transition-colors`}
            >
              {isCollapsed ? <ChevronsRight size={14} /> : <><ChevronsLeft size={14} /> Collapse</>}
            </button>
          )}

          <button
            onClick={logout}
            data-testid="logout-button"
            title={isCollapsed ? "Log out" : undefined}
            className={`w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg border border-white/15 text-slate-200 hover:bg-white/10 hover:text-white text-xs uppercase tracking-wider font-semibold transition-colors`}
          >
            <LogOut size={12} /> {!isCollapsed && "Log out"}
          </button>
        </div>
      </div>
    );
  };

  const desktopWidth = collapsed ? "w-16" : "w-64";
  const contentMargin = collapsed ? "lg:ml-16" : "lg:ml-64";

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 ${desktopWidth} flex-col z-30 no-print transition-[width] duration-200`}
        style={{ background: "linear-gradient(180deg, #0E1A33 0%, #0B1428 100%)" }}
        data-testid="desktop-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {sidebarInner(collapsed ? "desktop-collapsed" : "desktop-expanded")}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 no-print">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside
            className="absolute inset-y-0 left-0 w-64 flex flex-col"
            style={{ background: "linear-gradient(180deg, #0E1A33 0%, #0B1428 100%)" }}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-white"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            {sidebarInner("mobile")}
          </aside>
        </div>
      )}

      {/* Mobile top bar */}
      <header className="lg:hidden fixed inset-x-0 top-0 h-14 bg-white border-b border-slate-200 z-20 px-4 flex items-center justify-between no-print">
        <button onClick={() => setMobileOpen(true)} aria-label="Open menu" className="text-slate-700">
          <Menu size={20} />
        </button>
        <div className="font-display font-bold tracking-tight text-sm">Quote Matrix</div>
        <div className="w-5" />
      </header>

      {/* Content */}
      <main className={`flex-1 min-w-0 ${contentMargin} pt-14 lg:pt-0 transition-[margin] duration-200`}>
        <div className="max-w-[1500px] mx-auto p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
